import { bookData } from '../../../mocks/bookData';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function About() {
  const bookReveal = useScrollReveal({ threshold: 0.2 });
  const contentReveal = useScrollReveal({ threshold: 0.2 });
  const statsReveal = useScrollReveal({ threshold: 0.3 });

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-4 items-center">
          {/* Left side - Book image with fade-in from left */}
          <div className="lg:col-span-2" ref={bookReveal.ref}>
            <div className={`relative ${bookReveal.isVisible ? 'opacity-100 animate-fade-in-left' : 'opacity-0'}`}>
              <div className="bg-white rounded-2xl shadow-xl p-8 hover-lift">
                <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-full shadow-md text-sm font-semibold text-gray-700">
                  PDF Formatas
                </div>
                <img 
                  src={bookData.coverImage} 
                  alt="Keturi Vartai į Save knyga viršelis - astrologijos ir savęs pažinimo vadovas PDF formatu"
                  className="w-full rounded-lg"
                  loading="lazy"
                />
                <div className="absolute bottom-8 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 rounded-b-lg">
                  <p className="text-white text-center font-semibold">
                    {bookData.pages} puslapiai | {bookData.chapters} skyriai
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Content with fade-in from right */}
          <div className="lg:col-span-3 space-y-6" ref={contentReveal.ref}>
            <div className={`inline-block ${contentReveal.isVisible ? 'opacity-100 animate-fade-in-right stagger-1' : 'opacity-0'}`}>
              <span className="text-purple-600 font-semibold text-sm tracking-wider uppercase">
                APIE KNYGĄ
              </span>
            </div>
            
            <h2 className={`font-serif text-5xl lg:text-6xl font-bold text-gray-900 leading-tight ${contentReveal.isVisible ? 'opacity-100 animate-fade-in-right stagger-2' : 'opacity-0'}`}>
              Tavo Asmeninis Žemėlapis<br />
              Į Autentišką Gyvenimą
            </h2>
            
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p className={`font-medium text-gray-900 ${contentReveal.isVisible ? 'opacity-100 animate-fade-in-right stagger-3' : 'opacity-0'}`}>
                Tai ne dar viena knyga, kurią perskaitai ir pamiršti.
              </p>
              <p className={`${contentReveal.isVisible ? 'opacity-100 animate-fade-in-right stagger-4' : 'opacity-0'}`}>
                Tai praktinė kelionė per 4 stichijas, kuri padeda realiai jausti pokytį: mintyse, emocijose, santykiuose ir gyvenimo krypties jausme.
              </p>
              
              {/* Elements with icons - bounce-in animation */}
              <div className="space-y-4 pt-2">
                <div className={`flex items-start gap-4 ${contentReveal.isVisible ? 'opacity-100 animate-bounce-in stagger-5' : 'opacity-0'}`}>
                  <div className="text-3xl flex-shrink-0">🔥</div>
                  <div>
                    <span className="font-semibold text-gray-900">UGNIS</span>
                    <span className="text-gray-700"> – kai trūksta energijos, drąsos, veiksmų</span>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${contentReveal.isVisible ? 'opacity-100 animate-bounce-in stagger-6' : 'opacity-0'}`}>
                  <div className="text-3xl flex-shrink-0">🌬️</div>
                  <div>
                    <span className="font-semibold text-gray-900">ORAS</span>
                    <span className="text-gray-700"> – kai per daug minčių, neaišku ko nori</span>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${contentReveal.isVisible ? 'opacity-100 animate-bounce-in stagger-7' : 'opacity-0'}`}>
                  <div className="text-3xl flex-shrink-0">🌊</div>
                  <div>
                    <span className="font-semibold text-gray-900">VANDUO</span>
                    <span className="text-gray-700"> – kai trūksta ryšio, švelnumo, emocijų</span>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${contentReveal.isVisible ? 'opacity-100 animate-bounce-in stagger-8' : 'opacity-0'}`}>
                  <div className="text-3xl flex-shrink-0">🌿</div>
                  <div>
                    <span className="font-semibold text-gray-900">ŽEMĖ</span>
                    <span className="text-gray-700"> – kai trūksta stabilumo, rutinos, pagrindo</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats with hover lift and shimmer effect */}
            <div className="grid grid-cols-2 gap-6 pt-6" ref={statsReveal.ref}>
              <div className={`bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl hover-lift relative overflow-hidden ${statsReveal.isVisible ? 'opacity-100 animate-scale-in stagger-1' : 'opacity-0'}`}>
                <div className="absolute inset-0 animate-shimmer"></div>
                <div className="relative z-10">
                  <div className="text-4xl font-bold text-purple-600 mb-2">{bookData.chapters}</div>
                  <div className="text-gray-700 font-medium">Skyriai</div>
                  <div className="text-sm text-gray-600 mt-1">Struktūrizuotas turinys</div>
                </div>
              </div>
              <div className={`bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl hover-lift relative overflow-hidden ${statsReveal.isVisible ? 'opacity-100 animate-scale-in stagger-2' : 'opacity-0'}`}>
                <div className="absolute inset-0 animate-shimmer"></div>
                <div className="relative z-10">
                  <div className="text-4xl font-bold text-amber-600 mb-2">{bookData.exercises}+</div>
                  <div className="text-gray-700 font-medium">Pratimų</div>
                  <div className="text-sm text-gray-600 mt-1">Praktiniai įrankiai</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}